import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import {
  CrmInviteData,
  ExternalMemberInviteData,
  NetworkInviteLinkModel,
  NetworkInviteModel,
} from '../../types/invites';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import { ProfileField, ProfileDataType } from '../../types/profile';
import NoDataSharedModal from '../../components/modals/NoDataSharedModal';
import Button from '../../components/misc/Button';
import { connectionService, profileService } from '../../services';
import { SuccessPageState } from '../../types/navigation';
import useEffectAfterFirstRender from '../../hooks/useEffectAfterFirstRender';
import useAuth from '../../hooks/useAuth';
import { CommunityOverview } from '../../components/misc/CommunityOverview';

interface SharedWithCommunityMembersPageState {
  invite: NetworkInviteModel | NetworkInviteLinkModel | ExternalMemberInviteData | CrmInviteData;
  success: SuccessPageState;
}

export default function SharedWithCommunityMembersPage(): JSX.Element {
  const location = useLocation();
  const { invite, success }: SharedWithCommunityMembersPageState = location.state ?? {};
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();

  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [newFields, setNewFields] = useState<Partial<Record<ProfileDataType, ProfileField>>>({});
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!invite || !success) {
      toast.error(t('toast.error.general.inviteNotFound'));
      navigate('/', { replace: true });
    }
  }, []);

  useEffectAfterFirstRender(() => {
    if (auth) return;
    toast.error(t('toast.error.general.loggedOut'));
    navigate('/', { replace: true });
  }, [JSON.stringify(auth)]);

  const requesterName = invite?.name || '';

  const handleComplete = (): void => {
    if (selectedData.length === 0) {
      setModalOpen(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!invite?.communityId) {
      return;
    }
    // Save new fields first if any exist
    let allSelectedData = [...selectedData];
    const newFieldsToSave = Object.values(newFields).filter((field) => field && !field.id);

    if (newFieldsToSave.length > 0) {
      try {
        const savedFields = await profileService.addFields(newFieldsToSave);
        allSelectedData = [...selectedData, ...savedFields];
      } catch (error) {
        toast.error(t('toast.error.general.saveFailed'));
        return;
      }
    }

    connectionService
      .updateDataSharedWithCommunityMembers(
        invite.communityId,
        allSelectedData.map((d) => +d.id!),
      )
      .then(() => {
        navigate('/success', { state: success, replace: true });
      });
  };

  return (
    <>
      <ResponsePageWrapper>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}

        <div className="flex h-full flex-1 flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="mb-4 text-2xl font-semibold">{t('page.shared.subTitle')}</h2>
            <p className="text-sm">{t('page.dataForCommunityMembers', { name: requesterName })}</p>
          </div>

          <UserDataForm
            selectedData={selectedData}
            setSelectedData={setSelectedData}
            newFields={newFields}
            onNewFieldsChange={setNewFields}
            hasSelectAll
            preSelectAll
          />

          <div className="flex h-full min-h-10 flex-1 items-end justify-end gap-2 align-bottom">
            <Button variant="primary" className="px-[32px]" onClick={handleComplete}>
              {t('page.shared.shareData')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
      <NoDataSharedModal
        open={modalOpen}
        setOpen={setModalOpen}
        requesterName={requesterName}
        handleComplete={handleSubmit}
      />
    </>
  );
}
